import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// Note: Adding new translation keys for member actions

type Language = 'en' | 'sk';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'app-language';

// English translations
const en = {
  // Common
  loading: 'Loading...',
  signOut: 'Sign Out',
  back: 'Back',
  cancel: 'Cancel',
  continue: 'Continue',
  save: 'Save',
  
  // Landing page
  landingHeroTitle: '💯 Drinks in 365 Days.',
  landingHowItWorks: 'How it works?',
  landingStep1Title: 'Create your challenge',
  landingStep1Desc: 'One group. One year. One clear goal.',
  landingStep2Title: 'Invite friends',
  landingStep2Desc: 'Simple way to join your group.',
  landingStep3Title: 'Log drinks',
  landingStep3Desc: 'Track results. Time takes care of the rest.',
  landingCta: 'Start your challenge for free',
  landingDemoCta: 'Try 💯 Drinks without registration',
  landingSignIn: 'Already have an account? Sign in',
  
  // Demo mode
  demoIndicator: 'Demo version',
  demoCta: 'Start your 💯-day challenge for free',
  demoRewardTitle: 'Demo Reward',
  demoRewardDesc: 'This is how partner benefits look like',
  demoRewardExample: 'Demo reward – partner benefits appear here',
  
  // Auth
  authTitle: '💯 Drinks',
  authSubtitle: 'in 365 Days',
  authEmail: 'Email',
  authEmailPlaceholder: 'you@example.com',
  authPassword: 'Password',
  authPasswordPlaceholder: '••••••••',
  authPasswordRequirements: 'Password must be at least 8 characters long and include lowercase, uppercase, and numbers.',
  authSignIn: 'Sign In',
  authCreateAccount: 'Create Account',
  authPleaseWait: 'Please wait...',
  authNoAccount: "Don't have an account? Sign up",
  authHaveAccount: 'Already have an account? Sign in',
  authConsentLabel: 'I agree to the',
  authConsentLink: 'processing of personal data',
  authConsentRequired: 'You must agree to the processing of personal data.',
  authInvalidCredentials: 'Invalid email or password',
  authEmailNotConfirmed: 'Please confirm your email first',
  authUnableToSignIn: 'Unable to sign in. Please try again.',
  authAlreadyRegistered: 'This email is already registered. Please sign in instead.',
  authPasswordNotMeet: 'Password does not meet requirements.',
  authUnableToCreate: 'Unable to create account. Please try again.',
  authUnexpectedError: 'An unexpected error occurred',
  
  // Email confirmation
  confirmEmailTitle: 'Confirm your email',
  confirmEmailSent: 'We have sent a confirmation email to',
  confirmEmailInstruction: 'Click the link in the email to activate your account and continue.',
  confirmEmailRegistrationPart1: 'Click the link in the email to confirm your registration and consent to data processing according to the',
  confirmEmailRegistrationPart2: '.',
  privacyPolicyLabel: 'Privacy Policy',
  backToLogin: 'Back to login',
  
  // Consent modal
  consentModalTitle: 'Processing of personal data',
  consentTitle: 'Consent to personal data processing',
  consentIntro: 'I consent to the processing of my personal data in accordance with Regulation (EU) 2016/679 (GDPR).',
  consentOperator: 'Data controller',
  consentOperatorIntro: 'The personal data controller is:',
  consentCompanyName: 'Company name:',
  consentAddress: 'Address:',
  consentRegNumber: 'Registration number:',
  consentEmail: 'Contact email:',
  consentScope: 'Scope of processed personal data',
  consentScopeIntro: 'The controller processes primarily these personal data:',
  consentScopeEmail: 'email address,',
  consentScopeNickname: 'user nickname,',
  consentScopeUsage: 'data related to application usage (e.g., consumption records, settings, statistics).',
  consentPurpose: 'Purpose of processing',
  consentPurposeIntro: 'Personal data are processed for the purpose of:',
  consentPurposeAccount: 'creating and managing user account,',
  consentPurposeApp: 'enabling the use of the application and its features,',
  consentPurposeImprove: 'improving application functionality and user experience.',
  consentDuration: 'Duration of processing',
  consentDurationIntro: 'Personal data will be processed:',
  consentDurationAccount: 'for the duration of the user account, or',
  consentDurationRevoke: 'until this consent is revoked, unless special legal regulations require longer retention.',
  consentRights: 'Rights of the data subject',
  consentRightsIntro: 'I acknowledge that I have the right to:',
  consentRightsAccess: 'access my personal data,',
  consentRightsCorrect: 'rectification or erasure,',
  consentRightsRestrict: 'restriction of processing,',
  consentRightsPortability: 'data portability,',
  consentRightsComplaint: 'file a complaint with the supervisory authority.',
  consentConclusion: 'Consent is voluntary and I can revoke it at any time by contacting the controller at the email address above. Revocation of consent does not affect the lawfulness of processing of personal data before its revocation.',
  
  // Onboarding
  onboardingNicknameTitle: 'Choose Your Nickname',
  onboardingNicknameWarning: '⚠️ This will be your public identity and cannot be changed later.',
  onboardingNicknamePlaceholder: 'nickname',
  onboardingNicknameHint: '3-10 characters, letters, numbers, and underscores only',
  onboardingNicknameShort: 'Nickname must be 3-10 characters',
  onboardingNicknameInvalid: 'Nickname can only contain letters, numbers, and underscores',
  onboardingNicknameTaken: 'Nickname is already taken. Please choose a different one.',
  onboardingChecking: 'Checking...',
  onboardingNicknameSuccess: 'UserID set successfully!',
  
  onboardingJoinTitle: 'Join a Challenge',
  onboardingJoinDesc: 'Start or join a 365-day journey',
  onboardingCreateTitle: 'Your 365-day challenge starts at midnight 🕛',
  onboardingCreateDesc: '',
  onboardingJoinCodeTitle: 'Enter Invite Code',
  onboardingJoinCodeDesc: 'Enter the 6-character code from your friend',
  
  onboardingCreateBtn: 'Create New Challenge',
  onboardingJoinBtn: 'Join with Invite Code',
  onboardingCreateInfo: "You'll see the invite code for your group after tapping:",
  onboardingCreating: 'Creating...',
  onboardingCreateChallenge: 'Create Challenge',
  
  onboardingInviteCode: 'Invite Code',
  onboardingInviteCodePlaceholder: 'ABC123',
  onboardingJoining: 'Joining...',
  onboardingJoinChallenge: 'Join Challenge',
  onboardingInviteCodeLength: 'Invite code must be 6 characters',
  onboardingInvalidCode: 'Invalid invite code',
  onboardingChallengeEnded: 'This challenge has already ended',
  onboardingAlreadyInGroup: 'You are already in this group',
  onboardingJoinFailed: 'Failed to join group',
  onboardingJoinSuccess: 'Joined group successfully!',
  
  onboardingCreatedTitle: 'Challenge Created!',
  onboardingCreatedDesc: 'Share this code with friends to invite them',
  onboardingCopyCode: 'Copy Invite Code',
  onboardingCodeCopied: 'Invite code copied!',
  onboardingStartChallenge: 'Start Challenge',
  
  onboardingGenerateError: 'Failed to generate invite code',
  onboardingAlreadyActive: 'You already have an active challenge',
  onboardingCreateFailed: 'Failed to create group',
  onboardingCreateSuccess: 'Group created successfully!',
  
  // Invite modal
  inviteModalHeadline: 'Invite friends to your challenge',
  inviteModalShare: 'Share invite code',
  inviteModalCopyLink: 'Copy invite link',
  
  // Join page
  joinInvalidCodeTitle: 'Invalid Invite Code',
  joinInvalidCodeDesc: 'This invite code is no longer valid or has expired.',
  joinGoHome: 'Go to Home',
  joinAlreadyMemberTitle: "You're Already Here!",
  joinAlreadyMemberDesc: "You're already part of this challenge.",
  joinGoToDashboard: 'Go to Dashboard',
  joinHasOtherGroupTitle: 'Already in a Challenge',
  joinHasOtherGroupDesc: "You're already part of another active challenge. You need to leave your current challenge to join this one.",
  joinLeaveAndJoin: 'Leave current and join this one',
  joinConfirmLeaveTitle: 'Leave current challenge?',
  joinConfirmLeaveDesc: 'This will remove you from your current challenge. Your drink logs will remain in the group, but you will no longer be able to log drinks there.',
  joinConfirmLeaveAction: 'Leave and Join',
  joinSuccessTitle: 'Welcome to the Challenge!',
  joinSuccessDesc: "You've successfully joined the challenge.",
  joinRedirecting: 'Redirecting to dashboard...',
  joinBlockedTitle: "You can't join this challenge",
  joinBlockedDesc: "You're not able to join this challenge.",
  inviteModalCopied: 'Copied to clipboard',
  inviteModalHelper: 'Tugedr.com 💯 Drinks in 365 Days',
  
  // Completed challenge summary
  completedSummaryTitle: 'Start your next 365-day challenge',
  completedSummaryDesc: "Your previous challenge is complete. Here's a quick look back before you move on.",
  completedPeriod: 'Challenge period',
  completedTotalDrinks: 'Total drinks logged',
  completedLongestStreak: 'Longest streak',
  completedStatus: 'Status',
  completedLabel: 'Completed',
  
  // Recovery
  reportProblem: 'Report a problem',
  
  // Dashboard
  dashboardTitle: '💯 drinks',
  dashboardHi: 'Hi,',
  dashboardChallengeEnds: 'Your challenge',
  dashboardEndsIn: 'ends in',
  dashboardDays: 'days',
  dashboardDrinksLogged: 'drinks logged',
  dashboardRemaining: 'remaining',
  dashboardDoubleTap: 'Double-tap to log a drink ↑',
  dashboardTapAgain: 'Tap again',
  dashboardBeerLogged: 'Beer logged!',
  dashboardWineLogged: 'Wine logged!',
  dashboardShotLogged: 'Shot logged!',
  dashboardReachedLimit: "🏁 You've reached your 100-drink goal.",
  dashboardWaitForEnd: 'Wait for the challenge to end.',
  dashboardOverLimit: 'You exceeded 100 drinks. Logging continues.',
  dashboardComplete: 'Challenge Complete! 🎉',
  dashboardFinishedWith: 'You finished with',
  dashboardDrinksLoggedFinal: 'drinks logged.',
  
  // Drink info modal
  drinkInfoTitle: 'What counts as one drink:',
  drinkInfoBeer: '🍺 Beer: ≤ 0.5 l',
  drinkInfoWine: '🍷 Wine: ≤ 0.2 l',
  drinkInfoRule: 'Rule:',
  drinkInfoRuleText: 'The total amount of alcohol consumed during a given day or night is calculated and converted into a number of drinks based on alcohol type.',
  drinkInfoExamples: 'Examples:',
  drinkInfoExample1: '🍷 2 × 100 ml of wine',
  drinkInfoExample1Result: '= 1 wine (0.2 l)',
  drinkInfoExample2: '🍺 3 small beers (3 × 300 ml)',
  drinkInfoExample2Result: '= 2 beers (0.9 l < 2 × 0.5 l)',
  drinkInfoExample3: '🥃 1 Negroni (3 × 0.03 l)',
  drinkInfoExample3Result: '= 2 shots (0.09 l < 2 × 0.05 l)',
  
  // Drink guidance
  drinkGuidanceInitialTitle: 'Your first drink is on us! 🍻',
  drinkGuidanceInitialText: 'Try adding a drink by double-tapping the icon.',
  drinkGuidanceTrialTitle: 'Cheers!',
  drinkGuidanceTrialText: 'Your next double-tap will add your 1st real drink out of 💯',
  drinkTrialModalTitle: 'Great! 🎉',
  drinkTrialModalDesc: 'Now you know how it works. Your next double-tap will add your first real drink.',
  drinkTrialModalBtn: 'Got it',
  drinkInfoShot: '🥃 Shot / Cocktail: ≤ 0.05 l of alcohol',
  
  // Drink labels
  drinkBeer: 'Beer',
  drinkWine: 'Wine',
  drinkShot: 'Shot',
  
  // Leaderboard
  leaderboardTitle: 'Leaderboard',
  leaderboardDaysRemaining: 'days remaining',
  leaderboardComplete: 'Challenge complete!',
  leaderboardInviteCode: 'Invite code:',
  leaderboardRankings: 'Rankings',
  leaderboardUser: 'User',
  leaderboardTotal: 'Total',
  leaderboardYou: '(you)',
  leaderboardNoParticipants: 'No participants yet',
  
  // Profile
  profileTitle: 'Profile',
  profileChallengeProgress: 'Challenge Progress',
  profileDrinksLogged: 'Drinks Logged',
  profileRemaining: 'Remaining',
  profilePersonalStats: 'Personal Stats',
  profileDaysRemaining: 'days remaining in the challenge.',
  profileLastDrink: 'Last drink:',
  profileNoLastDrink: 'No drinks logged yet',
  profileCurrentStreak: 'Current streak:',
  profileLongestStreak: 'Longest streak:',
  profileDaysWithoutAlcohol: 'days without alcohol',
  profileWeekendStats: 'During the remaining',
  profileWeekends: 'weekends',
  profileYouCanDrink: 'you can drink',
  profileDrinks: 'drinks',
  profileThatsAbout: "— that's",
  profileDrinksPerWeekend: 'drinks per weekend',
  profileMonthlyOverview: 'Monthly Overview',
  profileMonth: 'Month',
  profilePrivacyNotice: 'Personal stats are private. Only challenge progress is visible.',
  
  // Rewards
  rewardsTitle: 'Rewards',
  rewardsSubtitle: 'Rewards unlock as your alcohol-free streak grows.',
  rewardUnlocked: 'Reward unlocked!',
  rewardLocked: 'Keep going to unlock',
  reward7days: '7 Days Alcohol-Free',
  reward14days: '14 Days Alcohol-Free',
  reward30days: '1 Month Alcohol-Free',
  reward60days: '2 Months Alcohol-Free',
  reward100days: '💯 Days Alcohol-Free',
  reward6months: '6 Months Alcohol-Free',
  reward300days: '300 Days Alcohol-Free',
  reward365days: '1 Year Alcohol-Free',
  rewardPartnerBenefit: 'Available benefit from our partner',
  rewardPartnerBenefitLine: 'Available benefit from our partner',
  rewardPartnerHeader: 'Partner reward',
  rewardPartnerBody1: 'This reward is unlocked because of your alcohol-free streak.',
  rewardPartnerBody2: 'Partner offers will appear here once available.',
  rewardPartnerCta: 'Learn more',
  partnerModalIntro1: 'Thanks for taking a break.',
  partnerModalIntro2: 'Our partner prepared a small benefit for this milestone.',
  partnerModalCta: 'Open partner website',
  rewardNoPartnerYet: 'Sorry, no rewards yet. 🥲',
  partnerInquiryTitle: 'Looking for the right partner.',
  partnerInquiryDesc: "We're looking for the right partner.\nIf you're interested, send us an email. We'd love to collaborate.",
  partnerInquiryCta: 'Partnership proposal',
  partnerInquiryEmailSubject: 'Partner 💯Drinks',
  partnerInquiryEmailBody: "Hi Peter from tugedr,\n\nI'm sending a partnership proposal with brand:\nContact person:\nPhone number:\n\nThank you.",
  
  // Health benefit modal
  rewardHealthBenefitLink: 'Reason to celebrate 🎈',
  rewardHealthBenefitLinkText: 'Reason to celebrate',
  healthBenefitCta: 'I understand',
  healthBenefitClose: 'Close',
  healthBenefitShowReward: 'Show reward',
  healthBenefitPhysicalLabel: 'Physical changes:',
  healthBenefitMentalLabel: 'Mental / behavioral changes:',
  
  // 7 days
  healthBenefit7Physical: '😴 Sleep quality begins to normalize\n💧 Body starts to rehydrate properly',
  healthBenefit7Mental: '🎭 Mood fluctuations may still occur\n🧠 Beginning of mental clarity improvement',
  healthBenefit7Source: 'Source: Sleep Foundation',
  healthBenefit7SourceLink: 'https://www.sleepfoundation.org/nutrition/alcohol-and-sleep',
  
  // 14 days
  healthBenefit14Physical: '🫃🏻 Digestive system starts to recover\n🧬 Reduced inflammation in the gut',
  healthBenefit14Mental: '🧠 Better emotional regulation\n😌 Reduced anxiety symptoms',
  healthBenefit14Source: 'Source: Gastroenterology Research',
  healthBenefit14SourceLink: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5513683/',
  
  // 30 days
  healthBenefit30Physical: '🫀 Liver begins regeneration process\n⚡ Improved metabolism and energy levels',
  healthBenefit30Mental: '💡 Clearer thinking and better focus\n😊 More stable mood patterns',
  healthBenefit30Source: 'Source: National Institute on Alcohol Abuse and Alcoholism (NIAAA)',
  healthBenefit30SourceLink: 'https://www.niaaa.nih.gov/publications/brochures-and-fact-sheets/alcohols-effects-body',
  
  // 60 days
  healthBenefit60Physical: '❤️ Cardiovascular health improves\n🩺 Blood pressure begins to normalize',
  healthBenefit60Mental: '💪 Stronger emotional resilience\n🧘 Better stress management',
  healthBenefit60Source: 'Source: American Heart Association',
  healthBenefit60SourceLink: 'https://www.heart.org/en/healthy-living/healthy-lifestyle/quit-smoking-tobacco-and-vaping/how-alcohol-affects-your-heart',
  
  // 100 days
  healthBenefit100Physical: '🧪 Dopamine receptors begin to normalize\n🔋 More natural energy throughout the day',
  healthBenefit100Mental: '🎯 Reward system recalibration\n✨ Healthier habit patterns forming',
  healthBenefit100Source: 'Source: Journal of Clinical Sleep Medicine',
  healthBenefit100SourceLink: 'https://jcsm.aasm.org/doi/10.5664/jcsm.7640',
  
  // 182 days (6 months)
  healthBenefit182Physical: '🛡️ Immune system significantly strengthened\n🔥 Reduced chronic inflammation',
  healthBenefit182Mental: '🧘 Improved stress response\n⚖️ Greater emotional stability',
  healthBenefit182Source: 'Source: Alcohol Research: Current Reviews',
  healthBenefit182SourceLink: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5513688/',
  
  // 300 days
  healthBenefit300Physical: '🏃 Sustained improvements in all body systems\n💨 Better physical endurance',
  healthBenefit300Mental: '🧩 Enhanced executive function\n🎯 Improved decision-making abilities',
  healthBenefit300Source: 'Source: The Lancet Public Health',
  healthBenefit300SourceLink: 'https://www.thelancet.com/journals/lanpub/article/PIIS2468-2667(18)30134-X/fulltext',
  
  // 365 days
  healthBenefit365Physical: '🏆 Full year of physical recovery\n📉 Significantly reduced health risks',
  healthBenefit365Mental: '🌟 Established healthy behavioral patterns\n🧠 Long-term neurological benefits',
  healthBenefit365Source: 'Source: World Health Organization (WHO)',
  healthBenefit365SourceLink: 'https://www.who.int/news-room/fact-sheets/detail/alcohol',

  // Info panel
  infoPanelTitle: 'Contact & Support',
  infoPanelWelcome: 'Welcome among the first users of this app. You can support its operation and further development via',
  infoPanelOr: 'or by starting the challenge',
  infoPanelWithFriends: 'with your friends. Thank you and cheers!',
  infoPanelContact: 'Partnerships, suggestions, observations, questions:',
  
  // Leave challenge
  leaveChallengeTitle: 'Challenge Membership',
  leaveChallengeCreator: 'You are the creator of challenge',
  leaveChallengeMember: 'You are currently part of challenge',
  leaveChallengeStarted: 'Your challenge started',
  leaveChallengeEnds: 'Challenge ends:',
  leaveChallengeCancel: 'Cancel Challenge',
  leaveChallengeCancelling: 'Cancelling...',
  leaveChallengeLeave: 'Leave Challenge',
  leaveChallengeLeaving: 'Leaving...',
  
  leaveDialogTitleCancel: 'Cancel this challenge?',
  leaveDialogDescCancel: 'As the creator, cancelling will',
  leaveDialogDescCancelBold: 'end the challenge for everyone',
  leaveDialogCancelPoint1: 'All members will be removed from this challenge',
  leaveDialogCancelPoint2: 'The challenge will be marked as completed',
  leaveDialogCancelPoint3: 'Drink logs will be preserved for historical reference',
  leaveDialogCancelPoint4: 'This action is',
  leaveDialogIrreversible: 'irreversible',
  leaveDialogConfirmCancel: 'Are you sure you want to cancel challenge',
  leaveDialogKeep: 'Keep Challenge',
  leaveDialogYesCancel: 'Yes, Cancel Challenge',
  
  leaveDialogTitleLeave: 'Leave this challenge?',
  leaveDialogDescLeave: 'This action is',
  leaveDialogDescLeaveIf: 'If you leave:',
  leaveDialogLeavePoint1: 'Your drink logs for this challenge will remain in the group',
  leaveDialogLeavePoint2: 'You will no longer be able to log drinks to this challenge',
  leaveDialogLeavePoint3: 'You can create or join a new challenge after leaving',
  leaveDialogConfirmLeave: 'Are you sure you want to leave challenge',
  leaveDialogYesLeave: 'Yes, Leave Challenge',
  
  leaveChallengeSuccess: 'You have left the challenge.',
  leaveChallengeFailed: 'Failed to leave challenge. Please try again.',
  cancelChallengeSuccess: 'Challenge has been cancelled for all members.',
  cancelChallengeFailed: 'Failed to cancel challenge. Please try again.',
  
  // Group members management
  groupMembersTitle: 'Challenge Members',
  groupMembersDesc: 'Manage members in your challenge',
  removeMemberDialogTitle: 'Remove member?',
  removeMemberDialogDesc: "This person will no longer be part of your challenge and won't be able to rejoin.",
  removeMemberConfirm: 'Remove',
  removeMemberRemoving: 'Removing...',
  removeMemberSuccess: 'Member removed.',
  removeMemberFailed: 'Failed to remove member. Please try again.',
  viewProfile: 'View profile',
  removeFromChallenge: 'Remove from challenge',
  
  // Hamburger menu
  menuLanguage: 'Language',
  menuFaq: 'Guide',
  menuContact: 'Contact & Support',
  menuSections: 'Sections',
  menuPrivacyPolicy: 'Privacy Policy',
  
  // Auth error for existing email during signup
  authEmailExistsTitle: 'Account already exists',
  authEmailExistsMessage: 'An account with this email already exists. Please sign in.',
  authEmailExistsCta: 'Go to Sign In',
  
  // Leaderboard single-member tooltip
  leaderboardTooltipHeadline: 'Your challenge starts at midnight.',
  leaderboardTooltipBeforeMidnightBody: 'How many friends will you manage to invite to your challenge',
  leaderboardTooltipAfterHeadline: 'Your 💯 Drinks challenge has started!',
  leaderboardTooltipAfterMidnightBody: 'With your crew, this will be a real challenge. 🍻 Invite friends:',
  
  // FAQ items
  faqQ1: 'What is the 100 Drinks Challenge?',
  faqA1: 'A private challenge where you and your friends commit to drinking no more than 100 alcoholic drinks in 365 days.',
  faqQ2: 'How do I log a drink?',
  faqA2: 'Double-tap on the drink icon (beer, wine, or shot) on the main screen to log it instantly.',
  faqQ3: 'What counts as one drink?',
  faqA3: 'What counts as one drink:\n\n🍺 Beer: ≤ 0.5 l\n🍷 Wine: ≤ 0.2 l\n🥃 Shot / Cocktail: ≤ 0.05 l of pure alcohol\n\nRule: The total amount of alcohol consumed during a given day or night is calculated and converted into a number of drinks based on alcohol type.\n\nExamples:\n🍷 2 × 100 ml of wine = 1 wine (0.2 l)\n🍺 3 small beers (3 × 300 ml) = 2 beers (0.9 l < 2 × 0.5 l)\n🥃 1 Negroni (3 × 0.03 l) = 2 shots (0.09 l < 2 × 0.05 l).',
  faqQ4: 'Can I edit or delete a logged drink?',
  faqA4: 'No, once logged, drinks cannot be changed. This keeps the challenge fair and honest.',
  faqQ5: 'What happens when the challenge ends?',
  faqA5: 'Your stats are saved and you can start a new challenge. Rewards you earned stay unlocked.',
  
  // Past challenges
  pastChallengesTitle: 'Past Challenges',
  pastChallengeEndedEarly: 'Ended early',
  pastChallengeCompleted: 'Completed',
  pastChallengeYourStats: 'Your Final Stats',
  pastChallengeTotalDrinks: 'Total Drinks',
  pastChallengeLongestStreak: 'Longest Streak',
  pastChallengeFinalLeaderboard: 'Final Leaderboard',
  
  // Password reset flow
  forgotPasswordLink: 'Forgot password?',
  resetPasswordTitle: 'Reset your password',
  resetPasswordSubtitle: "Enter the email address you used to sign up. We'll send you a link to reset your password.",
  resetPasswordEmailLabel: 'Email',
  resetPasswordSendLink: 'Send reset link',
  resetPasswordSuccessTitle: 'Check your email',
  resetPasswordSuccessMessage: "If an account with this email exists, we've sent you a password reset link.",
  resetPasswordBackToSignIn: 'Back to sign in',
  resetPasswordNewTitle: 'Set a new password',
  resetPasswordNewSubtitle: 'Enter your new password below.',
  resetPasswordNewPassword: 'New password',
  resetPasswordConfirm: 'Confirm new password',
  resetPasswordConfirmPlaceholder: 'Confirm your new password',
  resetPasswordMismatch: 'Passwords do not match',
  resetPasswordSubmit: 'Set new password',
  resetPasswordSuccess: 'Password updated successfully',
  resetPasswordError: 'Failed to update password. Please try again.',
  
  // Scheduled challenge
  scheduledTitle: 'Your challenge starts at midnight',
  scheduledHi: 'Hello',
  scheduledCountdown: '💯 drinks in 365 days from:',
  scheduledHours: 'hours',
  scheduledMinutes: 'min',
  scheduledSeconds: 'sec',
  scheduledInviteTitle: 'Invite your crew',
  scheduledInviteDesc: 'Share the code with friends so they can join the challenge before it begins.',
  scheduledTapToShare: 'Tap to share',
  scheduledShareNow: 'Share invite code',
  
  // History drink import
  historyLink: 'Add remembered (past 😉) drinks ←',
  historyTitle: 'Add Past Drinks',
  historyDateLabel: 'Date of last drink consumed',
  historyDatePlaceholder: 'Select date',
  historySubmit: 'Confirm drinks',
  historySubmitting: 'Saving...',
  historyClose: 'Close',
  historyErrorEmpty: 'Enter at least one drink or select a date.',
  historyErrorDateRequired: 'Date is required when drinks are entered.',
  historyErrorFailed: 'Failed to save history. Try again.',

  // Daily celebration
  dailyCelebrationHeadline: 'Every day a reason to celebrate',
  birthdayToday: 'Today celebrates birthday',
  birthdayTodayPlural: 'Today celebrate birthdays:',
  birthdayNearest: 'Nearest birthday:',

  // Footer
  footerCopyright: '© {year} tugedr s.r.o.',
};

// Slovak translations
const sk: typeof en = {
  // Common
  loading: 'Načítavam...',
  signOut: 'Odhlásiť sa',
  back: 'Späť',
  cancel: 'Zrušiť',
  continue: 'Pokračovať',
  save: 'Uložiť',
  
  // Landing page
  landingHeroTitle: '💯 Drinkov za 365 dní.',
  landingHowItWorks: 'Ako to funguje?',
  landingStep1Title: 'Založ výzvu',
  landingStep1Desc: 'Jedna skupina. Jeden rok. Jeden jasný cieľ.',
  landingStep2Title: 'Pozvi kamošov',
  landingStep2Desc: 'Jednoduché pripojenie do tvojej skupiny.',
  landingStep3Title: 'Eviduj drinky',
  landingStep3Desc: 'Sleduj výsledky. Čas sa postará o zvyšok.',
  landingCta: 'Začni svoju výzvu zadarmo',
  landingDemoCta: 'Vyskúšať 💯 Drinkov bez registrácie',
  landingSignIn: 'Máš už účet? Prihlás sa',
  
  // Demo mode
  demoIndicator: 'Ukážková verzia',
  demoCta: 'Začni svoju 💯dňovú výzvu zdarma',
  demoRewardTitle: 'Ukážková odmena',
  demoRewardDesc: 'Takto vyzerajú benefity od partnerov',
  demoRewardExample: 'Ukážková odmena – takto vyzerajú benefity od partnerov',
  
  // Auth
  authTitle: '💯 Drinkov',
  authSubtitle: 'za 365 dní',
  authEmail: 'E-mail',
  authEmailPlaceholder: 'tvoj@email.sk',
  authPassword: 'Heslo',
  authPasswordPlaceholder: '••••••••',
  authPasswordRequirements: 'Heslo musí mať aspoň 8 znakov a obsahovať malé, veľké písmená a čísla.',
  authSignIn: 'Prihlásiť sa',
  authCreateAccount: 'Vytvoriť účet',
  authPleaseWait: 'Čakaj prosím...',
  authNoAccount: 'Nemáš účet? Zaregistruj sa',
  authHaveAccount: 'Máš už účet? Prihlás sa',
  authConsentLabel: 'Súhlasím so',
  authConsentLink: 'spracovaním osobných údajov',
  authConsentRequired: 'Musíš súhlasiť so spracovaním osobných údajov.',
  authInvalidCredentials: 'Nesprávny e-mail alebo heslo',
  authEmailNotConfirmed: 'Najprv potvrď svoj e-mail',
  authUnableToSignIn: 'Prihlásenie zlyhalo. Skús to znova.',
  authAlreadyRegistered: 'Tento e-mail je už zaregistrovaný. Prihlás sa.',
  authPasswordNotMeet: 'Heslo nespĺňa požiadavky.',
  authUnableToCreate: 'Nepodarilo sa vytvoriť účet. Skús to znova.',
  authUnexpectedError: 'Nastala neočakávaná chyba',
  
  // Email confirmation
  confirmEmailTitle: 'Potvrďte svoj e-mail',
  confirmEmailSent: 'Na adresu sme odoslali potvrdzovací e-mail',
  confirmEmailInstruction: 'Kliknutím na odkaz v e-maile aktivujete svoj účet a budete môcť pokračovať.',
  confirmEmailRegistrationPart1: 'Kliknutím na odkaz v e-maile potvrdíte svoju registráciu, súhlas so zaradením do aplikácie a spracovaním osobných údajov podľa',
  confirmEmailRegistrationPart2: '.',
  privacyPolicyLabel: 'Zásad ochrany súkromia',
  backToLogin: 'Späť na prihlásenie',
  
  // Consent modal
  consentModalTitle: 'Spracovanie osobných údajov',
  consentTitle: 'Súhlas so spracovaním osobných údajov',
  consentIntro: 'Udeľujem súhlas so spracovaním mojich osobných údajov v súlade s Nariadením Európskeho parlamentu a Rady (EÚ) 2016/679 (GDPR).',
  consentOperator: 'Prevádzkovateľ',
  consentOperatorIntro: 'Prevádzkovateľom osobných údajov je:',
  consentCompanyName: 'Obchodné meno:',
  consentAddress: 'Sídlo:',
  consentRegNumber: 'IČO:',
  consentEmail: 'Kontaktný e-mail:',
  consentScope: 'Rozsah spracúvaných osobných údajov',
  consentScopeIntro: 'Prevádzkovateľ spracúva najmä tieto osobné údaje:',
  consentScopeEmail: 'e-mailová adresa,',
  consentScopeNickname: 'používateľská prezývka (nickname),',
  consentScopeUsage: 'údaje súvisiace s používaním aplikácie (napr. záznamy o konzumácii, nastavenia, štatistiky).',
  consentPurpose: 'Účel spracovania',
  consentPurposeIntro: 'Osobné údaje sú spracúvané za účelom:',
  consentPurposeAccount: 'vytvorenia a správy používateľského účtu,',
  consentPurposeApp: 'umožnenia používania aplikácie a jej funkcií,',
  consentPurposeImprove: 'zlepšovania funkčnosti aplikácie a používateľského zážitku.',
  consentDuration: 'Doba spracovania',
  consentDurationIntro: 'Osobné údaje budú spracúvané:',
  consentDurationAccount: 'po dobu trvania používateľského účtu, alebo',
  consentDurationRevoke: 'do odvolania tohto súhlasu, ak osobitné právne predpisy nevyžadujú ich dlhšie uchovávanie.',
  consentRights: 'Práva dotknutej osoby',
  consentRightsIntro: 'Beriem na vedomie, že mám právo:',
  consentRightsAccess: 'na prístup k svojim osobným údajom,',
  consentRightsCorrect: 'na ich opravu alebo vymazanie,',
  consentRightsRestrict: 'na obmedzenie spracovania,',
  consentRightsPortability: 'na prenosnosť údajov,',
  consentRightsComplaint: 'podať sťažnosť dozornému orgánu (Úrad na ochranu osobných údajov SR).',
  consentConclusion: 'Súhlas je dobrovoľný a môžem ho kedykoľvek odvolať kontaktovaním prevádzkovateľa na vyššie uvedenom e-maili. Odvolanie súhlasu nemá vplyv na zákonnosť spracovania osobných údajov pred jeho odvolaním.',
  
  // Onboarding
  onboardingNicknameTitle: 'Vyber si prezývku',
  onboardingNicknameWarning: '⚠️ Toto bude tvoja verejná identita a nebude ju možné zmeniť.',
  onboardingNicknamePlaceholder: 'prezývka',
  onboardingNicknameHint: '3-10 znakov, len písmená, čísla a podčiarkovníky',
  onboardingNicknameShort: 'Prezývka musí mať 3-10 znakov',
  onboardingNicknameInvalid: 'Prezývka môže obsahovať len písmená, čísla a podčiarkovníky',
  onboardingNicknameTaken: 'Prezývka je už obsadená. Zvoľ si inú.',
  onboardingChecking: 'Kontrolujem...',
  onboardingNicknameSuccess: 'Prezývka úspešne nastavená!',
  
  onboardingJoinTitle: 'Tvoja výzva 💯 drinkov',
  onboardingJoinDesc: 'Začni alebo sa pripoj k 365-dňovej ceste',
  onboardingCreateTitle: 'Tvoja 365-dňová výzva začne platiť od polnoci 🕛',
  onboardingCreateDesc: '',
  onboardingJoinCodeTitle: 'Zadaj kód pozvánky',
  onboardingJoinCodeDesc: 'Zadaj 6-znakový kód od kamaráta',
  
  onboardingCreateBtn: 'Vytvoriť novú výzvu',
  onboardingJoinBtn: 'Pripojiť sa kódom',
  onboardingCreateInfo: 'Pozývací kód pre tvoju partiu uvidíš po ťuknutí na:',
  onboardingCreating: 'Vytváram...',
  onboardingCreateChallenge: 'Vytvoriť výzvu',
  
  onboardingInviteCode: 'Kód pozvánky',
  onboardingInviteCodePlaceholder: 'ABC123',
  onboardingJoining: 'Pripájam sa...',
  onboardingJoinChallenge: 'Pripojiť sa',
  onboardingInviteCodeLength: 'Kód musí mať 6 znakov',
  onboardingInvalidCode: 'Neplatný kód pozvánky',
  onboardingChallengeEnded: 'Táto výzva už skončila',
  onboardingAlreadyInGroup: 'Už si v tejto skupine',
  onboardingJoinFailed: 'Nepodarilo sa pripojiť ku skupine',
  onboardingJoinSuccess: 'Úspešne pripojený!',
  
  onboardingCreatedTitle: 'Výzva vytvorená!',
  onboardingCreatedDesc: 'Zdieľaj tento kód s kamarátmi',
  onboardingCopyCode: 'Kopírovať kód',
  onboardingCodeCopied: 'Kód skopírovaný!',
  onboardingStartChallenge: 'Začať výzvu',
  
  onboardingGenerateError: 'Nepodarilo sa vygenerovať kód',
  onboardingAlreadyActive: 'Už máš aktívnu výzvu',
  onboardingCreateFailed: 'Nepodarilo sa vytvoriť skupinu',
  onboardingCreateSuccess: 'Skupina úspešne vytvorená!',
  
  // Invite modal
  inviteModalHeadline: 'Pozvi kamarátov do svojej výzvy',
  inviteModalShare: 'Zdieľať kód pozvánky',
  inviteModalCopyLink: 'Kopírovať odkaz',
  inviteModalCopied: 'Skopírované do schránky',
  inviteModalHelper: 'Tugedr.com 💯 Drinks in 365 Days',
  
  // Join page
  joinInvalidCodeTitle: 'Neplatný kód pozvánky',
  joinInvalidCodeDesc: 'Tento kód pozvánky už nie je platný alebo vypršal.',
  joinGoHome: 'Ísť domov',
  joinAlreadyMemberTitle: 'Už si tu!',
  joinAlreadyMemberDesc: 'Už si súčasťou tejto výzvy.',
  joinGoToDashboard: 'Ísť na prehľad',
  joinHasOtherGroupTitle: 'Už si vo výzve',
  joinHasOtherGroupDesc: 'Už si súčasťou inej aktívnej výzvy. Na pripojenie k tejto musíš najprv opustiť tú súčasnú.',
  joinLeaveAndJoin: 'Opustiť súčasnú a pripojiť sa',
  joinConfirmLeaveTitle: 'Opustiť súčasnú výzvu?',
  joinConfirmLeaveDesc: 'Toto ťa odstráni z tvojej súčasnej výzvy. Tvoje záznamy o drinkoch zostanú v skupine, ale už tam nebudeš môcť zapisovať drinky.',
  joinConfirmLeaveAction: 'Opustiť a pripojiť sa',
  joinSuccessTitle: 'Vitaj vo výzve!',
  joinSuccessDesc: 'Úspešne si sa pripojil k výzve.',
  joinRedirecting: 'Presmerovávam na prehľad...',
  joinBlockedTitle: 'Nemôžeš sa pripojiť k tejto výzve',
  joinBlockedDesc: 'Nie je možné sa pripojiť k tejto výzve.',
  
  // Completed challenge summary
  completedSummaryTitle: 'Začni ďalšiu 365-dňovú výzvu',
  completedSummaryDesc: 'Tvoja predchádzajúca výzva je dokončená. Tu je krátky prehľad pred ďalším krokom.',
  completedPeriod: 'Obdobie výzvy',
  completedTotalDrinks: 'Celkom drinkov',
  completedLongestStreak: 'Najdlhšia séria',
  completedStatus: 'Stav',
  completedLabel: 'Dokončená',
  
  // Recovery
  reportProblem: 'Nahlásiť problém',
  
  // Dashboard
  dashboardTitle: '💯 drinkov',
  dashboardHi: 'Ahoj,',
  dashboardChallengeEnds: 'Tvoja výzva',
  dashboardEndsIn: 'končí o',
  dashboardDays: 'dní',
  dashboardDrinksLogged: 'drinkov zapísaných',
  dashboardRemaining: 'zostáva',
  dashboardDoubleTap: 'Dvojité ťuknutie zapíše drink ↑',
  dashboardTapAgain: 'Ťukni znova',
  dashboardBeerLogged: 'Pivo zapísané!',
  dashboardWineLogged: 'Víno zapísané!',
  dashboardShotLogged: 'Panák zapísaný!',
  dashboardReachedLimit: '🏁 Dosiahol si svoj cieľ 100 drinkov.',
  dashboardWaitForEnd: 'Počkaj na koniec výzvy.',
  dashboardOverLimit: 'Prekročil si hranicu 100 drinkov. Evidencia pokračuje.',
  dashboardComplete: 'Výzva dokončená! 🎉',
  dashboardFinishedWith: 'Skončil si s',
  dashboardDrinksLoggedFinal: 'zapísanými drinkmi.',
  
  // Drink info modal
  drinkInfoTitle: 'Jeden drink podľa typu:',
  drinkInfoBeer: '🍺 Pivo: ≤ 0.5 l',
  drinkInfoWine: '🍷 Víno: ≤ 0.2 l',
  drinkInfoShot: '🥃 Panák / Koktejl: ≤ 0.05 l alkoholu',
  drinkInfoRule: 'Pravidlo:',
  drinkInfoRuleText: 'Počíta sa celkové množstvo vypitého alkoholu za daný deň/noc, ktoré sa spočíta podľa typu alkoholu na počet drinkov.',
  drinkInfoExamples: 'Ak napríklad vypiješ:',
  drinkInfoExample1: '🍷 2× 1 dl vína',
  drinkInfoExample1Result: '= 1 víno (0.2 l)',
  drinkInfoExample2: '🍺 3 malé pivá (3× 3 dl)',
  drinkInfoExample2Result: '= 2 pivá (0.9 l < 2× 0.5 l)',
  drinkInfoExample3: '🥃 1 Negroni (3× 0.03 l)',
  drinkInfoExample3Result: '= 2 panáky (0.09 l < 2× 0.05 l)',
  
  // Drink guidance
  drinkGuidanceInitialTitle: 'Prvý drink je na nás! 🍻',
  drinkGuidanceInitialText: 'Vyskúšaj si pridanie drinku 2× ťuknutím na ikonu.',
  drinkGuidanceTrialTitle: 'Na zdravie!',
  drinkGuidanceTrialText: 'Ďalším dvoj-ťuknutím už pridáš tvoj 1. vypitý drink zo 💯',
  drinkTrialModalTitle: 'Skvelé! 🎉',
  drinkTrialModalDesc: 'Teraz už vieš, ako to funguje. Ďalším 2× ťuknutím pridáš svoj prvý skutočný drink.',
  drinkTrialModalBtn: 'Rozumiem',
  
  // Drink labels
  drinkBeer: 'Pivo',
  drinkWine: 'Víno',
  drinkShot: 'Panák',
  
  // Leaderboard
  leaderboardTitle: 'Rebríček',
  leaderboardDaysRemaining: 'dní zostáva',
  leaderboardComplete: 'Výzva dokončená!',
  leaderboardInviteCode: 'Kód pozvánky:',
  leaderboardRankings: 'Poradie',
  leaderboardUser: 'Používateľ',
  leaderboardTotal: 'Celkom',
  leaderboardYou: '(ty)',
  leaderboardNoParticipants: 'Zatiaľ žiadni účastníci',
  
  // Profile
  profileTitle: 'Profil',
  profileChallengeProgress: 'Priebeh výzvy',
  profileDrinksLogged: 'Zapísané drinky',
  profileRemaining: 'Zostáva',
  profilePersonalStats: 'Osobné štatistiky',
  profileDaysRemaining: 'dní zostáva vo výzve.',
  profileLastDrink: 'Posledný drink:',
  profileNoLastDrink: 'Zatiaľ žiadne drinky',
  profileCurrentStreak: 'Aktuálna séria:',
  profileLongestStreak: 'Najdlhšia séria:',
  profileDaysWithoutAlcohol: 'dní bez alkoholu',
  profileWeekendStats: 'Počas zostávajúcich',
  profileWeekends: 'víkendov',
  profileYouCanDrink: 'môžeš vypiť',
  profileDrinks: 'drinkov',
  profileThatsAbout: '— to je',
  profileDrinksPerWeekend: 'drinkov za víkend',
  profileMonthlyOverview: 'Mesačný prehľad',
  profileMonth: 'Mesiac',
  profilePrivacyNotice: 'Osobné štatistiky sú súkromné. Viditeľný je len priebeh výzvy.',
  
  // Rewards
  rewardsTitle: 'Odmeny',
  rewardsSubtitle: 'Odmeny sa odomykajú podľa dĺžky obdobia bez alkoholu.',
  rewardUnlocked: 'Odmena odomknutá!',
  rewardLocked: 'Pokračuj pre odomknutie',
  reward7days: '7 dní bez alkoholu',
  reward14days: '14 dní bez alkoholu',
  reward30days: '1 mesiac bez alkoholu',
  reward60days: '2 mesiace bez alkoholu',
  reward100days: '💯 dní bez alkoholu',
  reward6months: '6 mesiacov bez alkoholu',
  reward300days: '300 dní bez alkoholu',
  reward365days: '1 rok bez alkoholu',
  rewardPartnerBenefit: 'Dostupná odmena od nášho partnera',
  rewardPartnerBenefitLine: 'Dostupná odmena od nášho partnera',
  rewardPartnerHeader: 'Odmena od partnera',
  rewardPartnerBody1: 'Táto odmena sa odomkla vďaka tvojmu obdobiu bez alkoholu.',
  rewardPartnerBody2: 'Ponuky partnerov sa tu zobrazia, keď budú dostupné.',
  rewardPartnerCta: 'Zistiť viac',
  partnerModalIntro1: 'Vďaka za prestávku.',
  partnerModalIntro2: 'Náš partner pripravil malú odmenu za tento míľnik.',
  partnerModalCta: 'Otvoriť web partnera',
  rewardNoPartnerYet: 'Prepáč, zatiaľ sme bez odmien. 🥲',
  partnerInquiryTitle: 'Hľadáme vhodného partnera.',
  partnerInquiryDesc: 'Hľadáme vhodného partnera.\nV prípade záujmu nám napíš email. Radi sa dohodneme na spolupráci.',
  partnerInquiryCta: 'Návrh na partnerstvo',
  partnerInquiryEmailSubject: 'Partner 💯Drinks',
  partnerInquiryEmailBody: 'Ahoj Peter z tugedr,\n\nposielam návrh na partnerstvo so značkou:\nKontaktná osoba:\nTel. číslo:\n\nĎakujem.',
  
  // Health benefit modal
  rewardHealthBenefitLink: 'Dôvod na oslavu 🎈',
  rewardHealthBenefitLinkText: 'Dôvod na oslavu',
  healthBenefitCta: 'Rozumiem',
  healthBenefitClose: 'Zatvoriť',
  healthBenefitShowReward: 'Zobraziť odmenu',
  healthBenefitPhysicalLabel: 'Fyzické zmeny:',
  healthBenefitMentalLabel: 'Mentálne / behaviorálne zmeny:',
  
  // 7 days
  healthBenefit7Physical: '😴 Kvalita spánku sa začína normalizovať\n💧 Telo sa začína správne hydratovať',
  healthBenefit7Mental: '🎭 Výkyvy nálady môžu stále pretrvávať\n🧠 Začiatok zlepšovania mentálnej jasnosti',
  healthBenefit7Source: 'Zdroj: Sleep Foundation',
  healthBenefit7SourceLink: 'https://www.sleepfoundation.org/nutrition/alcohol-and-sleep',
  
  // 14 days
  healthBenefit14Physical: '🫃🏻 Tráviaci systém sa začína zotavovať\n🧬 Znížený zápal v črevách',
  healthBenefit14Mental: '🧠 Lepšia emocionálna regulácia\n😌 Znížené príznaky úzkosti',
  healthBenefit14Source: 'Zdroj: Gastroenterology Research',
  healthBenefit14SourceLink: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5513683/',
  
  // 30 days
  healthBenefit30Physical: '🫀 Pečeň začína regeneračný proces\n⚡ Zlepšený metabolizmus a úroveň energie',
  healthBenefit30Mental: '💡 Jasnejšie myslenie a lepšia koncentrácia\n😊 Stabilnejšie vzorce nálady',
  healthBenefit30Source: 'Zdroj: National Institute on Alcohol Abuse and Alcoholism (NIAAA)',
  healthBenefit30SourceLink: 'https://www.niaaa.nih.gov/publications/brochures-and-fact-sheets/alcohols-effects-body',
  
  // 60 days
  healthBenefit60Physical: '❤️ Kardiovaskulárne zdravie sa zlepšuje\n🩺 Krvný tlak sa začína normalizovať',
  healthBenefit60Mental: '💪 Silnejšia emocionálna odolnosť\n🧘 Lepšie zvládanie stresu',
  healthBenefit60Source: 'Zdroj: American Heart Association',
  healthBenefit60SourceLink: 'https://www.heart.org/en/healthy-living/healthy-lifestyle/quit-smoking-tobacco-and-vaping/how-alcohol-affects-your-heart',
  
  // 100 days
  healthBenefit100Physical: '🧪 Dopamínové receptory sa začínajú normalizovať\n🔋 Prirodzenejšia energia počas dňa',
  healthBenefit100Mental: '🎯 Rekalibrácia systému odmien\n✨ Formovanie zdravších návykov',
  healthBenefit100Source: 'Zdroj: Journal of Clinical Sleep Medicine',
  healthBenefit100SourceLink: 'https://jcsm.aasm.org/doi/10.5664/jcsm.7640',
  
  // 182 days (6 months)
  healthBenefit182Physical: '🛡️ Imunitný systém výrazne posilnený\n🔥 Znížený chronický zápal',
  healthBenefit182Mental: '🧘 Zlepšená reakcia na stres\n⚖️ Väčšia emocionálna stabilita',
  healthBenefit182Source: 'Zdroj: Alcohol Research: Current Reviews',
  healthBenefit182SourceLink: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5513688/',
  
  // 300 days
  healthBenefit300Physical: '🏃 Trvalé zlepšenie všetkých telesných systémov\n💨 Lepšia fyzická výdrž',
  healthBenefit300Mental: '🧩 Zlepšené exekutívne funkcie\n🎯 Lepšie rozhodovacie schopnosti',
  healthBenefit300Source: 'Zdroj: The Lancet Public Health',
  healthBenefit300SourceLink: 'https://www.thelancet.com/journals/lanpub/article/PIIS2468-2667(18)30134-X/fulltext',
  
  // 365 days
  healthBenefit365Physical: '🏆 Celý rok fyzického zotavovania\n📉 Výrazne znížené zdravotné riziká',
  healthBenefit365Mental: '🌟 Ustálené zdravé behaviorálne vzorce\n🧠 Dlhodobé neurologické prínosy',
  healthBenefit365Source: 'Zdroj: World Health Organization (WHO)',
  healthBenefit365SourceLink: 'https://www.who.int/news-room/fact-sheets/detail/alcohol',

  // Info panel
  infoPanelTitle: 'Kontakt & Podpora',
  infoPanelWelcome: 'Vitaj medzi prvými užívateľmi tejto appky. Podporiť jej fungovanie a ďalší vývoj môžeš cez',
  infoPanelOr: 'alebo priamo vyhlásením výzvy',
  infoPanelWithFriends: 'spolu s tvojimi kamošmi. Ďakujeme a na zdravie!',
  infoPanelContact: 'Partnerstvá, návrhy, postrehy, otázky:',
  
  // Leave challenge
  leaveChallengeTitle: 'Členstvo vo výzve',
  leaveChallengeCreator: 'Si tvorcom výzvy',
  leaveChallengeMember: 'Si súčasťou výzvy',
  leaveChallengeStarted: 'Tvoja výzva začala',
  leaveChallengeEnds: 'Výzva končí:',
  leaveChallengeCancel: 'Zrušiť výzvu',
  leaveChallengeCancelling: 'Ruším...',
  leaveChallengeLeave: 'Opustiť výzvu',
  leaveChallengeLeaving: 'Opúšťam...',
  
  leaveDialogTitleCancel: 'Zrušiť túto výzvu?',
  leaveDialogDescCancel: 'Ako tvorca, zrušenie',
  leaveDialogDescCancelBold: 'ukončí výzvu pre všetkých',
  leaveDialogCancelPoint1: 'Všetci členovia budú odstránení z tejto výzvy',
  leaveDialogCancelPoint2: 'Výzva bude označená ako dokončená',
  leaveDialogCancelPoint3: 'Záznamy o drinkoch zostanú zachované',
  leaveDialogCancelPoint4: 'Táto akcia je',
  leaveDialogIrreversible: 'nezvratná',
  leaveDialogConfirmCancel: 'Naozaj chceš zrušiť výzvu',
  leaveDialogKeep: 'Ponechať výzvu',
  leaveDialogYesCancel: 'Áno, zrušiť výzvu',
  
  leaveDialogTitleLeave: 'Opustiť túto výzvu?',
  leaveDialogDescLeave: 'Táto akcia je',
  leaveDialogDescLeaveIf: 'Ak odídeš:',
  leaveDialogLeavePoint1: 'Tvoje záznamy o drinkoch zostanú v skupine',
  leaveDialogLeavePoint2: 'Nebudeš môcť zapisovať drinky do tejto výzvy',
  leaveDialogLeavePoint3: 'Môžeš vytvoriť alebo sa pripojiť k novej výzve',
  leaveDialogConfirmLeave: 'Naozaj chceš opustiť výzvu',
  leaveDialogYesLeave: 'Áno, opustiť výzvu',
  
  leaveChallengeSuccess: 'Opustil si výzvu.',
  leaveChallengeFailed: 'Nepodarilo sa opustiť výzvu. Skús to znova.',
  cancelChallengeSuccess: 'Výzva bola zrušená pre všetkých členov.',
  cancelChallengeFailed: 'Nepodarilo sa zrušiť výzvu. Skús to znova.',
  
  // Group members management
  groupMembersTitle: 'Členovia výzvy',
  groupMembersDesc: 'Spravuj členov vo svojej výzve',
  removeMemberDialogTitle: 'Odstrániť člena?',
  removeMemberDialogDesc: 'Táto osoba už nebude súčasťou tvojej výzvy a nebude sa môcť znova pripojiť.',
  removeMemberConfirm: 'Odstrániť',
  removeMemberRemoving: 'Odstraňujem...',
  removeMemberSuccess: 'Člen odstránený.',
  removeMemberFailed: 'Nepodarilo sa odstrániť člena. Skús to znova.',
  viewProfile: 'Zobraziť profil',
  removeFromChallenge: 'Odstrániť z výzvy',
  
  // Hamburger menu
  menuLanguage: 'Jazyk',
  menuFaq: 'Návod ako na to',
  menuContact: 'Kontakt & Podpora',
  menuSections: 'Sekcie',
  menuPrivacyPolicy: 'Ochrana súkromia',
  
  // Auth error for existing email during signup
  authEmailExistsTitle: 'Účet už existuje',
  authEmailExistsMessage: 'Účet s týmto e-mailom už existuje. Prihlás sa.',
  authEmailExistsCta: 'Prejsť na prihlásenie',
  
  // Leaderboard single-member tooltip
  leaderboardTooltipHeadline: 'Tvoja výzva začína o polnoci.',
  leaderboardTooltipBeforeMidnightBody: 'Koľko kamarátov stihneš pozvať do svojej výzvy',
  leaderboardTooltipAfterHeadline: 'Tvoja výzva 💯 drinkov začala!',
  leaderboardTooltipAfterMidnightBody: 'S tvojou partiou to bude skutočná výzva. 🍻 Pozvi kamarátov:',
  
  // FAQ items
  faqQ1: 'Čo je výzva 100 Drinkov?',
  faqA1: 'Súkromná výzva, kde sa s kamarátmi zaväzujete vypiť maximálne 100 alkoholických nápojov za 365 dní.',
  faqQ2: 'Ako zaznamenám drink?',
  faqA2: 'Dvojklik na ikonu nápoja (pivo, víno alebo shot) na hlavnej obrazovke ho okamžite zaznamená.',
  faqQ3: 'Čo sa počíta ako jeden drink?',
  faqA3: 'Jeden drink podľa typu:\n\n🍺 Pivo: ≤ 0.5 l\n🍷 Víno: ≤ 0.2 l\n🥃 Panák / Koktejl: ≤ 0.05 l alkoholu\n\nPravidlo: Počíta sa celkové množstvo vypitého alkoholu za daný deň/noc, ktoré sa spočíta podľa typu alkoholu na počet drinkov.\n\nAk napríklad vypiješ:\n🍷 2× 1 dl vína = 1 víno (0.2 l)\n🍺 3 malé pivá (3× 3 dl) = 2 pivá (0.9 l < 2× 0.5 l)\n🥃 1 Negroni (3× 0.03 l) = 2 panáky (0.09 l < 2× 0.05 l).',
  faqQ4: 'Môžem upraviť alebo vymazať zaznamenaný drink?',
  faqA4: 'Nie, raz zaznamenaný drink sa nedá zmeniť. To udržuje výzvu férovú a čestnú.',
  faqQ5: 'Čo sa stane keď výzva skončí?',
  faqA5: 'Tvoje štatistiky sa uložia a môžeš začať novú výzvu. Odmeny, ktoré si získal, ostávajú odomknuté.',
  
  // Past challenges
  pastChallengesTitle: 'Predošlé výzvy',
  pastChallengeEndedEarly: 'Ukončená skôr',
  pastChallengeCompleted: 'Dokončená',
  pastChallengeYourStats: 'Tvoje konečné štatistiky',
  pastChallengeTotalDrinks: 'Celkom drinkov',
  pastChallengeLongestStreak: 'Najdlhšia séria',
  pastChallengeFinalLeaderboard: 'Konečný rebríček',
  
  // Password reset flow
  forgotPasswordLink: 'Zabudnuté heslo?',
  resetPasswordTitle: 'Obnovenie hesla',
  resetPasswordSubtitle: 'Zadaj e-mailovú adresu, ktorú si použil/a pri registrácii. Pošleme ti odkaz na obnovenie hesla.',
  resetPasswordEmailLabel: 'E-mail',
  resetPasswordSendLink: 'Odoslať odkaz',
  resetPasswordSuccessTitle: 'Skontroluj e-mail',
  resetPasswordSuccessMessage: 'Ak účet s týmto e-mailom existuje, poslali sme ti odkaz na obnovenie hesla.',
  resetPasswordBackToSignIn: 'Späť na prihlásenie',
  resetPasswordNewTitle: 'Nastav si nové heslo',
  resetPasswordNewSubtitle: 'Zadaj nové heslo nižšie.',
  resetPasswordNewPassword: 'Nové heslo',
  resetPasswordConfirm: 'Potvrď nové heslo',
  resetPasswordConfirmPlaceholder: 'Zopakuj nové heslo',
  resetPasswordMismatch: 'Heslá sa nezhodujú',
  resetPasswordSubmit: 'Nastaviť nové heslo',
  resetPasswordSuccess: 'Heslo bolo úspešne aktualizované',
  resetPasswordError: 'Nepodarilo sa aktualizovať heslo. Skús to znova.',
  
  // Scheduled challenge
  scheduledTitle: 'Tvoja výzva začína o polnoci',
  scheduledHi: 'Ahoj',
  scheduledCountdown: '💯 drinkov za 365 dní od:',
  scheduledHours: 'hod',
  scheduledMinutes: 'min',
  scheduledSeconds: 'sek',
  scheduledInviteTitle: 'Pozvi tvoju partiu',
  scheduledInviteDesc: 'Zdieľaj kód s kamarátmi, aby sa mohli pridať do výzvy pred jej začiatkom.',
  scheduledTapToShare: 'Ťukni pre zdieľanie',
  scheduledShareNow: 'Zdieľať kód pozvánky',
  
  // History drink import
  historyLink: 'Pridať vypité (pamätané 😉) drinky v minulosti ←',
  historyTitle: 'Pridať drinky z minulosti',
  historyDateLabel: 'Dátum posledného vypitého drinku',
  historyDatePlaceholder: 'Vyber dátum',
  historySubmit: 'Potvrdiť drinky',
  historySubmitting: 'Ukladám...',
  historyClose: 'Zatvoriť',
  historyErrorEmpty: 'Zadaj aspoň jeden drink alebo vyber dátum.',
  historyErrorDateRequired: 'Dátum je povinný ak sú zadané drinky.',
  historyErrorFailed: 'Nepodarilo sa uložiť históriu. Skús znova.',

  // Daily celebration
  dailyCelebrationHeadline: 'Každý deň dôvod osláviť',
  birthdayToday: 'Dnes oslavuje narodeniny',
  birthdayTodayPlural: 'Dnes oslavujú narodeniny:',
  birthdayNearest: 'Najbližšie narodeniny:',

  // Footer
  footerCopyright: '© {year} tugedr s.r.o.',
};

const translations = { en, sk };

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored === 'sk' ? 'sk' : 'en') as Language;
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  const t = (key: string): string => {
    const value = translations[language][key as keyof typeof en];
    if (value === undefined) {
      console.warn(`Missing translation for key: ${key}`);
      return key;
    }
    return value;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
